import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Calendar, ArrowRight, Loader2 } from "lucide-react";
import { CONTACT } from "../data/content";
import { submitLead } from "../lib/api";

export default function Contact() {
  const location = useLocation();
  const rappel = location.hash === "#rappel";
  const [tab, setTab] = useState(rappel ? "callback" : "contact");
  const [form, setForm] = useState({ nom: "", telephone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({
        source: tab === "callback" ? "callback" : "contact",
        nom: form.nom,
        telephone: form.telephone,
        email: form.email || undefined,
        message: form.message || undefined,
      });
      setSent(true);
      toast.success(tab === "callback" ? "Nous vous rappelons dans 30 minutes !" : "Message envoyé, nous revenons vers vous rapidement.");
      setForm({ nom: "", telephone: "", email: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue. Merci d'appeler le " + CONTACT.phoneDisplay);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow">Contact</div>
          <h1 className="font-display uppercase text-white text-4xl md:text-6xl mt-4 leading-[0.95] tracking-tight">
            Parlons de<br/>votre <span className="text-[#7DD923]">projet</span>
          </h1>
          <p className="text-white/70 mt-6">
            Un conseiller vous répond sous 24h ouvrées. Étude gratuite et sans engagement.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`tel:${CONTACT.phoneLink}`} data-testid="contact-phone" className="flex items-start gap-4 group">
              <div className="w-11 h-11 rounded-full bg-[#7DD923]/10 border border-[#7DD923]/30 flex items-center justify-center group-hover:bg-[#7DD923] transition-colors">
                <Phone className="w-5 h-5 text-[#7DD923] group-hover:text-black" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/40">Téléphone</div>
                <div className="text-white font-semibold text-lg group-hover:text-[#7DD923] transition-colors">{CONTACT.phoneDisplay}</div>
                <div className="text-white/50 text-xs">Lun–Sam · 8h–19h</div>
              </div>
            </a>

            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-start gap-4 group">
              <div className="w-11 h-11 rounded-full bg-[#25D366]/10 border border-[#25D366]/30 flex items-center justify-center group-hover:bg-[#25D366] transition-colors">
                <MessageCircle className="w-5 h-5 text-[#25D366] group-hover:text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/40">WhatsApp</div>
                <div className="text-white font-semibold text-lg">Discussion instantanée</div>
              </div>
            </a>

            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-start gap-4 group">
              <div className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                <Mail className="w-5 h-5 text-white/80" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/40">Email</div>
                <div className="text-white font-semibold text-lg">{CONTACT.email}</div>
              </div>
            </a>

            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white/80" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/40">Zone d'intervention</div>
                <div className="text-white font-semibold">Alsace · Bas-Rhin · Haut-Rhin</div>
              </div>
            </div>

            <a href={CONTACT.calendly} target="_blank" rel="noopener noreferrer" data-testid="contact-calendly" className="btn-primary mt-4">
              <Calendar className="w-4 h-4" /> Prendre RDV en ligne
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="glass rounded-3xl p-6 md:p-10">
            <div className="flex gap-2 mb-6">
              <button
                data-testid="tab-contact"
                onClick={() => { setTab("contact"); setSent(false); }}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${tab === "contact" ? "bg-[#7DD923] text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
              >Envoyer un message</button>
              <button
                data-testid="tab-callback"
                onClick={() => { setTab("callback"); setSent(false); }}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${tab === "callback" ? "bg-[#7DD923] text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
              >Être rappelé en 30 min</button>
            </div>

            {sent ? (
              <div data-testid="contact-success" className="py-12 text-center">
                <div className="w-14 h-14 rounded-full bg-[#7DD923] flex items-center justify-center mx-auto mb-6">
                  <ArrowRight className="w-6 h-6 text-black" />
                </div>
                <h3 className="font-display text-white text-3xl uppercase">Message envoyé !</h3>
                <p className="text-white/70 mt-3">Nous vous recontactons très rapidement. Merci pour votre confiance.</p>
                <Link to="/" className="btn-secondary mt-6 inline-flex">Retour à l'accueil</Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid={`form-${tab}`}>
                <h2 className="font-display uppercase text-white text-2xl">
                  {tab === "callback" ? "Être rappelé sous 30 minutes" : "Envoyez-nous un message"}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required data-testid="contact-nom" placeholder="Nom & prénom *" value={form.nom} onChange={(e) => set("nom", e.target.value)} className="bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#7DD923] outline-none sm:col-span-2" />
                  <input required data-testid="contact-tel" placeholder="Téléphone *" value={form.telephone} onChange={(e) => set("telephone", e.target.value)} className="bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#7DD923] outline-none" />
                  <input type="email" data-testid="contact-mail" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#7DD923] outline-none" />
                </div>
                {tab === "contact" && (
                  <textarea data-testid="contact-message" placeholder="Votre message *" required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className="bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#7DD923] outline-none w-full resize-none" />
                )}
                <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-primary w-full sm:w-auto">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi</> : tab === "callback" ? "Me faire rappeler" : "Envoyer"}
                </button>
                <p className="text-xs text-white/40 leading-relaxed">
                  En envoyant ce formulaire, vous acceptez d'être recontacté par Optimeo Habitat. Vos données restent confidentielles.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
